<template>
    <div class="employees__inner">
        <ul class="filter">
            <li class="filter__item">
                <label for="employeesSearch" class="sr-only">{{ dictionary.EmployeesSearchInputPlaceholder }}</label>
                <input type="search" id="employeesSearch" v-model="query" :placeholder="dictionary.EmployeesSearchInputPlaceholder" />
            </li>
            <li class="filter__item" v-if="specializations.length > 1">
                <label for="specializationFilter" class="sr-only">{{ dictionary.EmployeesSpecializationSelectLabel }}</label>
                <select id="specializationFilter" v-model="specialization" :disabled="isLoading">
                    <option value="All">{{ dictionary.EmployeesSpecializationSelectLabel }}</option>
                    <option v-for="specializationItem in specializations" :key="specializationItem.Key"
                        :value="specializationItem.Key">{{
                            specializationItem.Name
                        }}
                    </option>
                </select>
            </li>
            <li class="filter__item" v-if="titles.length > 1">
                <label for="titleFilter" class="sr-only">{{ dictionary.EmployeesTitleSelectLabel
                }}</label>
                <select id="titleFilter" v-model="title" :disabled="isLoading">
                    <option value="All">{{ dictionary.EmployeesTitleSelectLabel }}</option>
                    <option v-for="titleItem in titles" :key="titleItem.Key" :value="titleItem.Key">{{
                        titleItem.Name
                    }}</option>
                </select>
            </li>
            <li class="filter__item" v-if="hasFilterApplied">
                <button type="button" @click="resetFilter">{{ dictionary.EmployeesResetFilter }}</button>
            </li>
        </ul>
        <ul class="employees__list" v-if="hasFilterApplied">
            <li class="employee-card" v-for="employee in employees" :key="employee.Url">
                <a :href="employee.Url" class="employee-card__inner">
                    <div class="employee-card__image">
                        <ResponsiveImage  v-if="employee.HasImageForVue" :imageModel="employee.ImageForVue" />
                    </div>
                    <div class="employee-card__info">
                        <div class="employee-card__row">
                            <h3 class="employee-card__name">{{ employee.Name }}</h3>
                            <p v-if="employee.HasTitle" class="employee-card__title">{{employee.Title}}</p>
                            <p v-if="employee.HasAreaOfResponsibility">{{employee.AreaOfResponsibility}}</p>
                        </div>
                        <div class="employee-card__row">
                        
                            <p v-if="employee.HasDirectPhone" class="employee-card__text">
                                {{dictionary.EmployeeMobilePhoneLabel}} {{employee.Mobile}}
                            </p>
                            <p v-if="employee.HasPhone" class="employee-card__text">
                                {{dictionary.EmployeePhoneLabel}} {{employee.Phone}}
                            </p>
                            <p v-if="employee.HasEmail" class="employee-card__text">
                                {{employee.Email}}
                            </p>
                        </div>
                    </div>
                </a>
            </li>
        </ul>
        <div class="employees__footer" v-if="hasFilterApplied">
            <div class="employees__no-employees" v-show="!employees.length && !isLoading">
                {{ dictionary.EmployeesNoResults }}
            </div>
            <div class="employees__load-more" v-show="hasMoreEmployees">
                <a @click="getNextPage" :disabled="isLoading">{{
                    dictionary.EmployeesLoadMore
                }}</a>
            </div>
        </div>
    </div>
</template>

<script>
import { nextTick } from 'vue'
import { bindRoute } from "./mixins/bindroute.js";
import ResponsiveImage from "./components/responsiveImage.vue";


const serviceUrl = "/umbraco/api/employeesApi/getEmployees",
    defaultSpecialization = "All",
    defaultTitle = "All",
    serverListEvent = "employeesFilterStateChanged";

var queryTimer;

export default {
    name: "employees",
    components: {
        ResponsiveImage
    },
    data() {
        return {
            dictionary: [],
            preselectedLocationNodeId: null,
            titles: [],
            specializations: [],
            employees: [],
            pageSize: 16,
            totalEmployees: 0,
            loadedEmployees: 0,
            isLoading: false,
            hasLoadedInitials: false,
            employeesNodeId: 0,
            isResetting: false
        };
    },
    mixins: [
        bindRoute({
            page: 1,
            query: null,
            specialization: defaultSpecialization,
            title: defaultTitle,
        }),
    ],
    watch: {
        hasFilterApplied: {
            immediate: true,
            handler(isFiltered) {
                // The server rendered list owns the unfiltered view; Vue owns everything else.
                window.dispatchEvent(new CustomEvent(serverListEvent, { detail: !!isFiltered }));
            }
        },
        page: {
            handler(oldVal, newVal) {
                this.handleFilterPropertyChange(oldVal, newVal, false, true);
            }
        },
        query: {
            handler(newVal, oldVal) {
                clearTimeout(queryTimer);
                queryTimer = setTimeout(() => {
                    this.handleFilterPropertyChange(oldVal, newVal, true, false);
                }, 750);
            }
        },
        specialization: {
            handler(oldVal, newVal) {
                this.handleFilterPropertyChange(oldVal, newVal, true, false);
            }
        },
        title: {
            handler(oldVal, newVal) {
                this.handleFilterPropertyChange(oldVal, newVal, true, false);
            }
        }
    },
    mounted() {
        this.employeesNodeId = this.$parent.employeesNodeId;
        this.preselectedLocationNodeId = this.$parent.preselectedLocationNodeId;

        this.$router.isReady().then(() => {
            this.getInitialEmployees();
        }).catch(error => {
            console.log(error);
        });
    },
    methods: {
        handleFilterPropertyChange(oldVal, newVal, resetPage = false, append = false) {
            if (oldVal !== newVal) {
                if (this.hasLoadedInitials && !this.isResetting) {
                    this.filterChanged(resetPage, append);
                }
            }
        },
        getEmployees(amountToSkip, pageSize, append = false) {
            const vm = this,
                languageCode = document.querySelector("html").getAttribute("lang");
            vm.toggleSpinner(true);
            var object = {
                employeesNodeId: vm.employeesNodeId,
                languageCode: languageCode,
                amountToSkip: amountToSkip,
                pageSize: pageSize,
            };

            if (vm.preselectedLocationNodeId) {
                object.preselectedLocationNodeId = vm.preselectedLocationNodeId;
            }

            if (vm.query) {
                object.query = vm.query;
            }

            if (vm.specialization != defaultSpecialization) {
                object.specialization = vm.specialization;
            }

            if (vm.title != defaultTitle) {
                object.title = vm.title;
            }

            fetch(serviceUrl + "?" + new URLSearchParams(object))
                .then((response) => response.json())
                .then((data) => {

                    if (append) {
                        vm.employees.push(...data.Items);
                        vm.loadedEmployees += data.Items.length;
                    } else {
                        vm.employees = data.Items;
                        vm.loadedEmployees = data.Items.length;
                    }
                    vm.totalEmployees = data.TotalItems;

                    vm.dictionary = data.Dictionary;
                    vm.specializations = data.Facets.Specializations;
                    vm.titles = data.Facets.Titles;

                    nextTick(() => {
                        vm.hasLoadedInitials = true;
                        vm.toggleSpinner(false);
                        vm.isResetting = false;
                    });

                })
                .catch((error) => {
                    console.log(error);
                    vm.toggleSpinner(false);
                });
        },
        getNextPage() {
            if (!this.isLoading) {
                this.page++;
            }
        },
        getInitialEmployees() {
            // Get all cases up to, and including, the current page
            this.getEmployees(0, this.page * this.pageSize, true);

        },
        filterChanged(resetPage = false, append = false) {
            this.isResetting = true;
            if (!append || resetPage) {
                this.page = 1;
                this.getEmployees(0, this.pageSize, append);
            } else {
                this.getEmployees(this.pageSize * (this.page - 1), this.pageSize, append);
            }
        },
        resetFilter() {
            const vm = this;
            vm.isResetting = true;
            vm.employees = [];
            vm.totalEmployees = 0;
            vm.loadedEmployees = 0;
            vm.page = 1;
            vm.specialization = defaultSpecialization;
            vm.title = defaultTitle;

            vm.$router.replace({ path: window.location.pathname, query: {} })
                .then(() => {
                    vm.getEmployees(0, vm.pageSize);
                })
                .catch(err => { console.log(err); });
        },
        toggleSpinner: function (isLoading) {
            this.isLoading = isLoading;
            const bodyElement = document.querySelector("body");
            if (isLoading) {
                bodyElement.classList.add("is-loading");
            } else {
                bodyElement.classList.remove("is-loading");
            }
        },
    },
    computed: {
        hasMoreEmployees() {
            return (
                this.totalEmployees !== 0 &&
                this.employees.length < this.totalEmployees
            );
        },
        hasFilterApplied() {
            return this.query || this.title != defaultTitle ||
                this.specialization != defaultSpecialization;
        }
    },
};
</script>